// 🗑 DamageManagementPanel — v187 (4 أدوار مستقلة، فرع-واعٍ).
//
// New workflow (v187):
//   pending_manager  → BM approves → pending_finance
//   pending_finance  → accountant approves → approved_final
//   approved_final   → optional bulk archive → archived
//   rejected         → terminal
//
// أدوار الأشخاص:
//   branch_manager   : يعتمد pending_manager الخاص بفرعه فقط.
//   accountant       : يعتمد pending_finance عبر كل الفروع.
//   operations       : مراقب فقط عبر كل الفروع، بلا اعتماد (v187 shift).
//   admin            : كامل الصلاحيات.
import React, { useEffect, useState } from "react";
import { api } from "./lib/api";
import { showToast as showToastRaw } from "./lib/theme";
import { DamageCard } from "./MyWorkPanel";
import ProductCostPricesPanel from "./ProductCostPricesPanel";

const showToast = (msg, kind = "info") => {
  try { showToastRaw(msg, kind); } catch (_e) { console.log(`[${kind}]`, msg); }
};

// v187 — 4 صيغ للتبويبات حسب الدور:
//   BM       (branch_manager)  → يعتمد pending_manager فقط، يرى فرعه فقط.
//   FIN      (accountant)      → يعتمد pending_finance، يرى الجميع.
//   OPS_OBS  (operations)      → مراقب فقط عبر كل الفروع، بلا اعتماد.
//   ADMIN    (admin)           → كامل الصلاحيات + تبويب الأرشيف.
const BM_TABS = [
  { id: "pending_manager", label: "⏳ بانتظار اعتمادي" },
  { id: "pending_finance", label: "✓ صعد للمالية" },
  { id: "rejected",        label: "❌ مرفوضة" },
];
const FINANCE_TABS = [
  { id: "pending_finance", label: "⏳ بانتظار اعتماد المالية" },
  { id: "approved_final",  label: "📦 معتمدة (للأرشفة/التصدير)" },
  { id: "rejected",        label: "❌ مرفوضة" },
];
const OPS_OBSERVER_TABS = [
  { id: "pending_manager", label: "⏳ في مرحلة المدير" },
  { id: "pending_finance", label: "⏳ في مرحلة المالية" },
  { id: "approved_final",  label: "✓ معتمدة نهائيًا" },
  { id: "rejected",        label: "❌ مرفوضة" },
];

export default function DamageManagementPanel({ user }) {
  const role = user?.role || "";
  const isAdmin    = role === "admin";
  const isFinance  = role === "accountant";
  const isOps      = role === "operations";
  const isBM       = role === "branch_manager";

  const tabs = isAdmin
    ? [...FINANCE_TABS, ...OPS_OBSERVER_TABS.filter(t => !FINANCE_TABS.find(f => f.id === t.id)),
        { id: "archived", label: "📦 المؤرشفة (حذف)" }]
    : isFinance ? FINANCE_TABS
    : isOps ? OPS_OBSERVER_TABS
    : BM_TABS;

  const [tab, setTab] = useState(tabs[0].id);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(new Set());
  const [busyArchive, setBusyArchive] = useState(false);
  const [branchFilter, setBranchFilter] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const params = { status: tab, limit: 200 };
      if (branchFilter.trim()) params.branch_id = branchFilter.trim();
      const { data } = await api.get("/damage-requests", { params });
      setItems(data.items || []);
      setSelected(new Set());
    } catch (e) { showToast(e?.response?.data?.detail || e?.message || "خطأ", "error"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [tab, branchFilter]);

  // v187 — تحديد الدور المستخدَم من DamageCard لعرض الأزرار الصحيحة.
  const cardRole = isBM ? "branch_manager"
                 : isFinance ? "accountant"
                 : isOps ? "operations_observer"
                 : isAdmin ? (
                     tab === "pending_manager" ? "branch_manager"
                       : tab === "pending_finance" || tab === "approved_final" ? "accountant"
                       : "operations_observer"
                   )
                 : "barista";
  const canApprove = isBM || isFinance || isAdmin; // Ops لا يعتمد

  const toggleSel = (id) => {
    const s = new Set(selected);
    if (s.has(id)) s.delete(id); else s.add(id);
    setSelected(s);
  };
  const selectAll = () => setSelected(new Set(items.map((x) => x.id)));
  const clearSel  = () => setSelected(new Set());

  const archive = async (fmt) => {
    if (selected.size === 0) {
      const ok = window.confirm(`لم تحدد أي طلب — هل تريد أرشفة جميع المعتمدة (${items.length}) دفعةً واحدة؟`);
      if (!ok) return;
    }
    const title = window.prompt("عنوان الأرشيف (اختياري — يظهر في الملف):",
      `ملفات الإتلاف المعتمدة — ${new Date().toISOString().slice(0, 10)}`);
    if (title === null) return; // cancelled
    setBusyArchive(true);
    try {
      const body = { format: fmt, title: title.trim() };
      if (selected.size > 0) body.ids = Array.from(selected);
      const { data } = await api.post("/damage-requests/bulk-archive", body);
      showToast(`✅ تم إنشاء الأرشيف (${data.item_count} طلب) — يظهر الآن في صفحة التالف`, "success");
      load();
    } catch (e) { showToast(e?.response?.data?.detail || e?.message || "خطأ", "error"); }
    finally { setBusyArchive(false); }
  };

  const showArchiveBar = (isFinance || isAdmin) && (tab === "approved_final" || tab === "approved") && items.length > 0;

  return (
    <div data-testid="damage-mgmt-panel" style={{ padding: 14 }}>
      <div style={{ background: "linear-gradient(135deg, #6b4423 0%, #b8860b 100%)", color: "#fff8e6",
        padding: "14px 18px", borderRadius: 10, marginBottom: 14 }}>
        <div style={{ fontSize: 18, fontWeight: 800 }}>🗑 إدارة طلبات الإتلاف</div>
        <div style={{ fontSize: 12, opacity: 0.9, marginTop: 4 }}>
          {isOps
            ? "دور مراقب — رؤية شاملة عبر كل الفروع، بلا اعتماد (v187)"
            : isFinance
              ? "اعتماد المالية النهائي — بعد اعتمادك تُخصم القيمة وتظهر في بطاقة غرفة القيادة"
              : "اعتمد طلبات إتلاف فرعك ثم تنتقل للمالية للاعتماد النهائي"}
        </div>
      </div>

      {/* v187 — إدارة أسعار التكلفة (للأدمن + operations الذين يملكون operations.products) */}
      {(isAdmin || isOps) && <ProductCostPricesPanel />}

      <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 12, alignItems: "center" }}>
        {tabs.map((t) => (
          <button key={t.id} onClick={() => setTab(t.id)} data-testid={`dmg-tab-${t.id}`}
            style={{
              padding: "8px 14px", borderRadius: 999,
              background: tab === t.id ? "var(--brown-deep)" : "#fff",
              color: tab === t.id ? "#fff" : "var(--brown-deep)",
              border: `1px solid ${tab === t.id ? "var(--brown-deep)" : "var(--border)"}`,
              fontWeight: 700, cursor: "pointer", fontSize: 13,
            }}>
            {t.label}
          </button>
        ))}
        {(isOps || isFinance || isAdmin) && (
          <div style={{ display: "flex", alignItems: "center", gap: 6, marginInlineStart: "auto" }}>
            <span style={{ fontSize: 11.5, color: "var(--muted)", fontWeight: 700 }}>فلترة branch_id:</span>
            <input value={branchFilter} onChange={(e) => setBranchFilter(e.target.value)}
              placeholder="مثل: al_badrani" data-testid="dmg-branch-filter"
              style={{ padding: "6px 10px", border: "1px solid var(--border)", borderRadius: 6, fontSize: 12, width: 160 }} />
            {branchFilter && (
              <button onClick={() => setBranchFilter("")} data-testid="dmg-branch-filter-clear"
                style={{ ...btnSm, background: "#fff", border: "1px solid var(--border)", color: "#6B7280" }}>
                ×
              </button>
            )}
          </div>
        )}
      </div>

      {/* Bulk archive bar (operations only, only on approved tab) */}
      {showArchiveBar && (
        <div data-testid="bulk-archive-bar"
          style={{ background: "#FEF3C7", border: "1px solid #D97706", borderRadius: 8,
            padding: 12, marginBottom: 12, display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
          <div style={{ flex: 1, minWidth: 220 }}>
            <div style={{ fontSize: 13, fontWeight: 800, color: "#92400E" }}>
              📦 جاهزة للتصدير الدفعي ({selected.size} من {items.length})
            </div>
            <div style={{ fontSize: 11.5, color: "#78350F", marginTop: 2 }}>
              صدّر كل المعتمدة في ملف واحد للمكتب المحاسبي. بعد التصدير ستنتقل لصفحة التالف.
            </div>
          </div>
          <button onClick={selectAll} data-testid="dmg-select-all"
            style={{ ...btnSm, background: "#fff", border: "1px solid #92400E", color: "#92400E" }}>
            تحديد الكل
          </button>
          {selected.size > 0 && (
            <button onClick={clearSel} data-testid="dmg-clear-sel"
              style={{ ...btnSm, background: "#fff", border: "1px solid #6B7280", color: "#6B7280" }}>
              إلغاء التحديد
            </button>
          )}
          <button onClick={() => archive("excel")} disabled={busyArchive} data-testid="dmg-archive-xlsx"
            style={{ ...btnSm, background: "#0891B2", color: "#fff" }}>
            {busyArchive ? "..." : "📊 Excel فقط"}
          </button>
          <button onClick={() => archive("pdf")} disabled={busyArchive} data-testid="dmg-archive-pdf"
            style={{ ...btnSm, background: "#DC2626", color: "#fff" }}>
            {busyArchive ? "..." : "📄 PDF فقط"}
          </button>
          <button onClick={() => archive("both")} disabled={busyArchive} data-testid="dmg-archive-both"
            style={{ ...btnSm, background: "#16A34A", color: "#fff", fontSize: 13, padding: "8px 16px" }}>
            {busyArchive ? "جارٍ التصدير..." : "📦 تصدير وأرشفة (Excel + PDF)"}
          </button>
        </div>
      )}

      {loading && <div className="card" style={{ padding: 20, textAlign: "center" }}>جارٍ التحميل...</div>}
      {!loading && items.length === 0 && (
        <div className="card" style={{ padding: 30, textAlign: "center", color: "var(--muted)" }}>
          لا توجد طلبات في هذه القائمة
        </div>
      )}
      {!loading && items.map((r) => (
        <div key={r.id} style={{ display: "flex", gap: 8, alignItems: "flex-start" }}>
          {(isFinance || isAdmin) && (tab === "approved_final" || tab === "approved") && (
            <input type="checkbox" checked={selected.has(r.id)} onChange={() => toggleSel(r.id)}
              data-testid={`dmg-sel-${r.id}`}
              style={{ marginTop: 18, transform: "scale(1.4)", cursor: "pointer" }} />
          )}
          <div style={{ flex: 1, minWidth: 0 }}>
            <DamageCard req={r} canApprove={canApprove} role={cardRole} onChanged={load} isAdmin={isAdmin} />
          </div>
        </div>
      ))}
    </div>
  );
}

const btnSm = {
  padding: "6px 12px", border: "none", borderRadius: 4,
  fontSize: 12, fontWeight: 700, cursor: "pointer",
};
